/**
 * Minimal, dependency-free Markdown renderer.
 *
 * Supports the subset used across the Studio (onboarding docs, ontology
 * cards, ad-hoc help): # / ## / ### / #### headings, paragraphs,
 * blockquotes ('> '), - bullet lists, 1. ordered lists, ```fenced code
 * blocks```, `inline code`, **bold**, *italic*, [text](url) links, and
 * GFM | tables |.
 *
 * Not a full CommonMark parser; do not feed user-submitted markdown to it.
 */
import type React from "react";

export function Markdown({ source }: { source: string }) {
  const blocks = parseBlocks(source ?? "");
  return <>{blocks.map((b, i) => renderBlock(b, i))}</>;
}

type Block =
  | { kind: "h1" | "h2" | "h3" | "h4"; text: string }
  | { kind: "p"; text: string }
  | { kind: "quote"; text: string }
  | { kind: "code"; text: string; lang: string }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] }
  | { kind: "table"; headers: string[]; rows: string[][] }
  | { kind: "hr" };

function parseBlocks(src: string): Block[] {
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  const out: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    const fence = line.match(/^```(\w*)\s*$/);
    if (fence) {
      const lang = fence[1] ?? "";
      const buf: string[] = [];
      i++;
      while (i < lines.length && !/^```\s*$/.test(lines[i])) { buf.push(lines[i]); i++; }
      i++;
      out.push({ kind: "code", lang, text: buf.join("\n") });
      continue;
    }

    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) {
      const level = h[1].length as 1 | 2 | 3 | 4;
      out.push({ kind: (`h${level}` as "h1" | "h2" | "h3" | "h4"), text: h[2] });
      i++; continue;
    }

    if (/^\s*---+\s*$/.test(line)) { out.push({ kind: "hr" }); i++; continue; }
    if (/^\s*$/.test(line)) { i++; continue; }

    if (line.startsWith("> ")) {
      const buf: string[] = [];
      while (i < lines.length && lines[i].startsWith("> ")) {
        buf.push(lines[i].slice(2)); i++;
      }
      out.push({ kind: "quote", text: buf.join(" ") });
      continue;
    }

    if (line.startsWith("|") && i + 1 < lines.length && /^\|[\s:|-]+\|\s*$/.test(lines[i + 1])) {
      const splitRow = (s: string) =>
        s.replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
      const headers = splitRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i].startsWith("|")) {
        rows.push(splitRow(lines[i])); i++;
      }
      out.push({ kind: "table", headers, rows });
      continue;
    }

    if (/^[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*]\s+/, "")); i++;
      }
      out.push({ kind: "ul", items });
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, "")); i++;
      }
      out.push({ kind: "ol", items });
      continue;
    }

    const buf: string[] = [];
    while (i < lines.length && !/^\s*$/.test(lines[i]) && !lines[i].startsWith("#")
            && !lines[i].startsWith("```") && !lines[i].startsWith("> ")
            && !/^[-*]\s+/.test(lines[i]) && !/^\d+\.\s+/.test(lines[i])
            && !lines[i].startsWith("|")) {
      buf.push(lines[i]); i++;
    }
    if (buf.length) out.push({ kind: "p", text: buf.join(" ") });
  }
  return out;
}

function renderBlock(b: Block, key: number) {
  switch (b.kind) {
    case "h1": return <h1 key={key} className="mt-6 mb-3 text-2xl font-semibold">{inline(b.text)}</h1>;
    case "h2": return <h2 key={key} className="mt-6 mb-2 text-xl font-semibold border-b border-border pb-1">{inline(b.text)}</h2>;
    case "h3": return <h3 key={key} className="mt-4 mb-2 text-base font-semibold">{inline(b.text)}</h3>;
    case "h4": return <h4 key={key} className="mt-3 mb-1 text-sm font-semibold uppercase tracking-wide text-muted">{inline(b.text)}</h4>;
    case "p":  return <p key={key} className="my-2 text-sm leading-relaxed">{inline(b.text)}</p>;
    case "quote":
      return <blockquote key={key} className="my-3 border-l-2 border-accent/60 bg-bg/50 px-3 py-2 text-sm italic text-muted">{inline(b.text)}</blockquote>;
    case "code":
      return (
        <pre key={key} className="my-3 overflow-x-auto rounded border border-border bg-bg p-3 text-xs leading-snug">
          <code>{b.text}</code>
        </pre>
      );
    case "ul":
      return (
        <ul key={key} className="my-2 list-disc pl-6 text-sm leading-relaxed">
          {b.items.map((it, j) => <li key={j}>{inline(it)}</li>)}
        </ul>
      );
    case "ol":
      return (
        <ol key={key} className="my-2 list-decimal pl-6 text-sm leading-relaxed">
          {b.items.map((it, j) => <li key={j}>{inline(it)}</li>)}
        </ol>
      );
    case "table":
      return (
        <div key={key} className="my-3 overflow-x-auto">
          <table className="min-w-full border-collapse text-sm">
            <thead className="bg-border/30 text-left text-xs uppercase tracking-wide text-muted">
              <tr>{b.headers.map((h, j) =>
                <th key={j} className="border-b border-border px-2 py-1.5 font-medium">{inline(h)}</th>)}
              </tr>
            </thead>
            <tbody>
              {b.rows.map((row, j) => (
                <tr key={j} className="border-b border-border/50">
                  {row.map((c, k) =>
                    <td key={k} className="px-2 py-1.5 align-top">{inline(c)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "hr":
      return <hr key={key} className="my-4 border-border" />;
  }
}

function inline(text: string): React.ReactNode {
  const out: React.ReactNode[] = [];
  let rest = text;
  let key = 0;
  const re = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(\[[^\]]+\]\([^)]+\))/;
  while (rest.length) {
    const m = re.exec(rest);
    if (!m) { out.push(rest); break; }
    if (m.index > 0) out.push(rest.slice(0, m.index));
    const tok = m[0];
    if (tok.startsWith("`")) {
      out.push(<code key={key++} className="rounded bg-bg/70 px-1 py-0.5 font-mono text-[0.85em]">{tok.slice(1, -1)}</code>);
    } else if (tok.startsWith("**")) {
      out.push(<strong key={key++}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith("*")) {
      out.push(<em key={key++}>{tok.slice(1, -1)}</em>);
    } else if (tok.startsWith("[")) {
      const lm = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (lm) {
        out.push(
          <a key={key++} href={lm[2]} target="_blank" rel="noreferrer"
             className="text-accent underline-offset-2 hover:underline">{lm[1]}</a>
        );
      } else {
        out.push(tok);
      }
    }
    rest = rest.slice(m.index + tok.length);
  }
  return <>{out}</>;
}
